import { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { useSEO } from "@/lib/useSEO";
import { langAlternates, langCanonical } from "@/lib/langSeo";
import { article as articleSchema, breadcrumb, itemList } from "@/lib/jsonLd";
import {
  findArticleBySlug,
  CATEGORY_LABEL_KEYS,
  fetchArticleBySlug,
  fetchAllArticles,
} from "@/data/journalArticles";
import MarkdownRenderer from "@/components/journal/MarkdownRenderer";
import NewsletterDisclosure from "@/components/NewsletterDisclosure";
import { pickLocale } from "@/lib/localize";
import { TranslationFallbackNotice } from "@/components/TranslationFallbackNotice";
import { extractHeadings } from "@/lib/headings";

const APPSTORE_URL = "https://apps.apple.com/app/virtue-guide/id6777389425";

const DATE_LOCALES = { nl: "nl-NL", en: "en-US", de: "de-DE", fr: "fr-FR", es: "es-ES" };

function stripFrontMatter(md) {
  if (typeof md !== "string") return "";
  return md.replace(/^---[\s\S]*?---\s*/, "");
}

export default function JournalArticle() {
  const { slug } = useParams();
  const { t, lang } = useT();
  const [article, setArticle] = useState(() => findArticleBySlug(slug));
  const [resolvedNotFound, setResolvedNotFound] = useState(false);
  const [allArticles, setAllArticles] = useState([]);

  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // 1. Resolve the article: try the file-based registry first, then the API
  useEffect(() => {
    const fileEntry = findArticleBySlug(slug);
    if (fileEntry) {
      setArticle(fileEntry);
      return;
    }
    let cancelled = false;
    fetchArticleBySlug(slug).then((doc) => {
      if (cancelled) return;
      if (doc) setArticle(doc);
      else setResolvedNotFound(true);
    });
    return () => { cancelled = true; };
  }, [slug]);

  // 2. Load the body
  useEffect(() => {
    if (!article) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(false);

    // DB-backed articles carry the body inline (Markdown per locale)
    if (article._source === "db" || (article.body && (article.body.en || article.body.nl))) {
      const md = pickLocale(article.body, lang);
      setBody(md);
      setLoading(false);
      return () => { cancelled = true; };
    }

    // File-based fallback: fetch the markdown from /public/content/journal/…
    const url = `${process.env.PUBLIC_URL || ""}/content/journal/${article.slug}/${lang}.md`;
    const otherLang = lang === "nl" ? "en" : "nl";
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((text) => {
        if (cancelled) return;
        setBody(stripFrontMatter(text));
        setLoading(false);
      })
      .catch(async () => {
        try {
          const fallback = await fetch(
            `${process.env.PUBLIC_URL || ""}/content/journal/${article.slug}/${otherLang}.md`
          );
          if (!fallback.ok) throw new Error("fallback failed");
          const text = await fallback.text();
          if (cancelled) return;
          setBody(stripFrontMatter(text));
          setLoading(false);
        } catch {
          if (cancelled) return;
          setLoadError(true);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [article, lang]);

  // 3. Fetch the merged article list for "related" section
  useEffect(() => {
    fetchAllArticles().then(setAllArticles).catch(() => setAllArticles([]));
  }, []);

  const title = article ? pickLocale(article.title, lang) : "";
  const excerpt = article ? pickLocale(article.excerpt, lang) : "";
  const metaDescription = article ? (pickLocale(article.metaDescription, lang) || excerpt) : "";
  const author = article ? pickLocale(article.author, lang) : "";
  const authorRole = article ? pickLocale(article.authorRole, lang) : "";
  const categoryLabel = article ? t(CATEGORY_LABEL_KEYS[article.category]) : "";

  const dateLocale = DATE_LOCALES[lang] || "en-US";
  const dateLong = article
    ? new Date(article.publishedAt).toLocaleDateString(dateLocale, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  const url = article ? `/stoic-journal/${article.slug}` : "/stoic-journal";
  const canonical = `https://stoicos.ai${url}`;

  // Table-of-contents ItemList schema (deep-link every H2/H3 to its slug).
  // Same URL serves EN + NL, so a single ItemList is emitted per active locale.
  const tocItems = useMemo(() => extractHeadings(body), [body]);

  // Multilingual availability. We treat a translation as "present" only when
  // BOTH the title AND the body carry non-empty text in that locale — this
  // prevents an EN-only article from falsely claiming an NL translation
  // just because some minor field slipped through with a stray character.
  const hasLocale = (loc) => {
    if (!article) return false;
    const titleField = article.title;
    const bodyField = article.body;
    const t = typeof titleField === "object" && titleField ? (titleField[loc] || "").trim() : "";
    const b = typeof bodyField === "object" && bodyField ? (bodyField[loc] || "").trim() : "";
    return !!t && !!b;
  };
  const activeLangHasContent = hasLocale(lang);
  const otherLang = lang === "en" ? "nl" : "en";
  const otherLangHasContent = hasLocale(otherLang);
  // Convention: EN is always the source (AutoSEO originates in English).
  // When the active locale is NL and EN exists, this NL article is the
  // translation → `translationOfWork` points to EN. When the active locale
  // is EN and NL exists, EN is still the source → `workTranslation` points
  // to NL. Fallback rendering (locale missing) suppresses the pairing so
  // Google is never told a non-existent translation is present.
  let translationRelation;
  if (activeLangHasContent && otherLangHasContent) {
    translationRelation = {
      inLanguage: otherLang,
      headline: pickLocale(article.title, otherLang),
      role: lang === "en" ? "translation" : "source",
    };
  }

  useSEO({
    title: article ? `${title} | StoicOs Journal` : "Article | StoicOs Journal",
    description: metaDescription,
    canonical: article
      ? langCanonical(url, hasLocale(lang) ? lang : hasLocale("en") ? "en" : "nl")
      : canonical,
    // Real per-language URLs (?lang=) — the cluster only lists languages in
    // which this article genuinely exists (title AND body non-empty).
    alternates: article
      ? langAlternates(url, ["en", "nl", "de", "fr", "es"].filter(hasLocale))
      : undefined,
    og: article
      ? {
          title: `${title} | StoicOs Journal`,
          description: metaDescription,
          image: article.cover,
          imageWidth: article.coverMeta?.width,
          imageHeight: article.coverMeta?.height,
          imageType: article.coverMeta?.mime,
          type: "article",
        }
      : undefined,
    jsonLd: article
      ? [
          articleSchema({
            headline: title,
            description: metaDescription,
            url,
            image: article.cover,
            datePublished: article.publishedAt,
            authorName: author,
            authorRole,
            articleSection: categoryLabel,
            inLanguage: activeLangHasContent ? lang : undefined,
            translation: translationRelation,
          }),
          breadcrumb([
            { name: "Home", url: "/" },
            { name: t("journal.title"), url: "/stoic-journal" },
            { name: title, url },
          ]),
          ...(tocItems.length > 0
            ? [
                itemList({
                  name: `Table of contents: ${title}`,
                  url,
                  items: tocItems,
                }),
              ]
            : []),
        ]
      : [],
  });

  // 404 only after we've tried both the file registry AND the API
  if (!article && resolvedNotFound) {
    return <Navigate to="/stoic-journal" replace />;
  }
  if (!article) {
    return (
      <main className="relative pt-32 md:pt-40 pb-28 max-w-3xl mx-auto px-6" data-testid="journal-article-loading">
        <p className="text-bone/60 text-center py-20">···</p>
      </main>
    );
  }

  // Related articles: same category, exclude current, top 3
  const related = allArticles
    .filter((a) => a.slug !== article.slug && a.category === article.category)
    .slice(0, 3);
  const relatedFallback = allArticles.filter((a) => a.slug !== article.slug).slice(0, 3);
  const relatedList = related.length > 0 ? related : relatedFallback;

  return (
    <main data-testid="journal-article-page" className="relative pt-32 md:pt-40 pb-28">
      {/* Cover hero — "contain" articles show the image integrally (title/figures
          baked into the artwork must never be cropped away) */}
      {article.coverFit === "contain" ? (
        <header className="relative w-full mb-10 md:mb-14" data-testid="article-hero">
          <div className="max-w-6xl mx-auto md:px-10">
            <img
              src={article.cover}
              alt={title}
              data-testid="article-hero-image"
              className="w-full h-auto md:border md:border-white/10"
            />
          </div>
          <div className="px-6 md:px-10 mt-10 md:mt-14">
            <div className="max-w-4xl mx-auto">
              <Link
                to="/stoic-journal"
                data-testid="article-back-link"
                className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-gold/90 hover:text-bone transition-colors mb-6"
              >
                <ArrowLeft size={12} strokeWidth={1.5} /> {t("journal.backToIndex")}
              </Link>
              <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-5">
                {categoryLabel}
              </p>
              <h1
                data-testid="article-title"
                className="font-serif text-bone text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
              >
                {title}
              </h1>
              <p className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] tracking-[0.22em] uppercase text-fog/85">
                <span data-testid="article-author">{author}</span>
                <span className="text-bone/30">·</span>
                <span data-testid="article-date">
                  {t("journal.article.publishedOn")} {dateLong}
                </span>
                <span className="text-bone/30">·</span>
                <span className="inline-flex items-center gap-2">
                  <Clock size={12} strokeWidth={1.5} />
                  {article.readingTimeMin} {t("journal.readingTime")}
                </span>
                {article.articleReference && (
                  <>
                    <span className="text-bone/30">·</span>
                    <span data-testid="article-reference" className="text-fog/70">
                      Article {article.articleReference}
                    </span>
                  </>
                )}
              </p>
            </div>
          </div>
        </header>
      ) : (
      <header
        className="relative w-full h-[55vh] min-h-[420px] max-h-[640px] overflow-hidden mb-12 md:mb-16"
        data-testid="article-hero"
      >
        <img
          src={article.cover}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden
          style={{
            background:
              "linear-gradient(180deg, rgba(7,6,5,0.65) 0%, rgba(7,6,5,0.35) 40%, rgba(7,6,5,0.95) 100%)",
          }}
        />
        <div className="absolute inset-x-0 bottom-0 px-6 md:px-10 pb-12 md:pb-16">
          <div className="max-w-4xl mx-auto">
            <Link
              to="/stoic-journal"
              data-testid="article-back-link"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-gold/90 hover:text-bone transition-colors mb-6"
            >
              <ArrowLeft size={12} strokeWidth={1.5} /> {t("journal.backToIndex")}
            </Link>
            <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-5">
              {categoryLabel}
            </p>
            <h1
              data-testid="article-title"
              className="font-serif text-bone text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
            >
              {title}
            </h1>
            <p className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] tracking-[0.22em] uppercase text-fog/85">
              <span data-testid="article-author">{author}</span>
              <span className="text-bone/30">·</span>
              <span data-testid="article-date">
                {t("journal.article.publishedOn")} {dateLong}
              </span>
              <span className="text-bone/30">·</span>
              <span className="inline-flex items-center gap-2">
                <Clock size={12} strokeWidth={1.5} />
                {article.readingTimeMin} {t("journal.readingTime")}
              </span>
              {article.articleReference && (
                <>
                  <span className="text-bone/30">·</span>
                  <span data-testid="article-reference" className="text-fog/70">
                    Article {article.articleReference}
                  </span>
                </>
              )}
            </p>
          </div>
        </div>
      </header>
      )}

      {/* Body */}
      <article className="max-w-3xl mx-auto px-6 md:px-10" data-testid="article-body">
        {loading && (
          <p className="text-bone/60 text-center py-20" data-testid="article-loading">
            ···
          </p>
        )}
        {loadError && (
          <p
            data-testid="article-load-error"
            className="text-bone/70 text-center py-20 font-reading"
          >
            {lang === "nl"
              ? "Het artikel kon niet worden geladen. Probeer het opnieuw."
              : "The article could not be loaded. Please try again."}
          </p>
        )}
        {!loading && !loadError && article && (
          <TranslationFallbackNotice fields={[article.title, article.excerpt, article.body]} lang={lang} />
        )}
        {!loading && !loadError && <MarkdownRenderer source={body} />}

        {/* Tags */}
        {article.tags && article.tags.length > 0 && (
          <div className="mt-12 flex flex-wrap items-center gap-2" data-testid="article-tags">
            <span className="text-[10px] tracking-[0.3em] uppercase text-gold/80 mr-2">
              {t("journal.article.tagsLabel")}
            </span>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="text-[11px] tracking-[0.14em] uppercase text-bone/75 border border-white/10 px-2.5 py-1"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Signature */}
        <div className="mt-14 pt-8 border-t border-white/10 flex items-center gap-4">
          <span className="h-px w-8 bg-gold/60" aria-hidden />
          <div>
            <p className="font-serif italic text-bone text-lg">{author}</p>
            <p className="text-[11px] tracking-[0.22em] uppercase text-fog/80 mt-1">
              {authorRole}
            </p>
          </div>
        </div>
      </article>

      {/* Virtue Guide CTA — repeats after every article */}
      <section
        data-testid="article-vg-cta"
        className="max-w-3xl mx-auto px-6 md:px-10 mt-20 md:mt-24"
      >
        <div className="relative overflow-hidden border border-gold/40 bg-[#050505]">
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none opacity-70"
            style={{
              background:
                "radial-gradient(ellipse at 20% 0%, rgba(212,175,55,0.10) 0%, rgba(0,0,0,0) 55%)",
            }}
          />
          <div className="relative p-8 md:p-12 flex flex-col items-center text-center gap-6">
            <p className="text-[10px] tracking-[0.32em] uppercase text-gold">
              {t("journal.cta.eyebrow")}
            </p>
            <h3 className="font-serif text-bone text-2xl md:text-3xl tracking-tight leading-tight max-w-xl">
              {t("journal.cta.title")}
            </h3>
            <p className="font-reading text-bone/80 text-base md:text-lg leading-[1.75] max-w-md">
              {t("journal.cta.body")}
            </p>
            <a
              href={APPSTORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="article-vg-cta-apple"
              aria-label="Download Virtue Guide on the App Store"
              className="inline-block transition-transform duration-300 hover:scale-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60 mt-2"
              style={{ lineHeight: 0 }}
            >
              <img
                src={lang === "nl" ? "/badges/app-store-nl.svg" : "/badges/app-store-en.svg"}
                alt="Download on the App Store"
                width={162}
                height={54}
                style={{ height: "54px", width: "auto", display: "block" }}
                draggable={false}
              />
            </a>
            <p className="text-[10px] tracking-[0.28em] uppercase text-fog/70 mt-1">
              {t("journal.cta.download")}
            </p>
          </div>
        </div>
      </section>

      {/* Newsletter CTA — the only visible subscribe form on article pages */}
      <NewsletterDisclosure variant="article" />

      {/* Related articles */}
      {relatedList.length > 0 && (
        <section
          data-testid="article-related"
          className="max-w-6xl mx-auto px-6 md:px-10 mt-20 md:mt-24"
        >
          <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-8">
            {t("journal.article.relatedTitle")}
          </p>
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {relatedList.map((a) => (
              <li key={a.slug}>
                <Link
                  to={`/stoic-journal/${a.slug}`}
                  data-testid={`article-related-${a.slug}`}
                  className="group flex items-center justify-between gap-4 p-5 border border-white/10 hover:border-gold/40 hover:bg-white/[0.02] transition-all duration-300 h-full"
                >
                  <span className="font-serif text-lg text-bone group-hover:text-gold transition-colors">
                    {pickLocale(a.title, lang)}
                  </span>
                  <ArrowRight
                    size={14}
                    strokeWidth={1.5}
                    className="text-gold opacity-70 group-hover:opacity-100 transition-opacity flex-shrink-0"
                  />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </main>
  );
}
